import { Link } from 'react-router-dom';
import './Footer.css';
import { trackEvent } from '../../utils/tracking';
import Wordmark from '../ui/Wordmark';
import StoreButtons from '../ui/StoreButtons';
import { Facebook, Instagram, Linkedin, Mail } from 'lucide-react';

function XIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

const socials = [
    { name: "LinkedIn", href: "https://www.linkedin.com/company/accruewise/", Icon: Linkedin },
    { name: "X", href: "https://x.com/accruewise", Icon: XIcon },
    { name: "Instagram", href: "https://www.instagram.com/accruewise/", Icon: Instagram },
    { name: "Facebook", href: "https://www.facebook.com/accruewise/", Icon: Facebook },
];

const columns = [
    {
        title: "Product",
        links: [
            { label: "All features", to: "/features" },
            { label: "Spend tracker", to: "/features#track" },
            { label: "Split bills", to: "/features#split" },
            { label: "Bills & recharges", to: "/features#pay" },
            { label: "Prepaid RuPay card", to: "/card" },
            { label: "Marketplace", to: "/marketplace" },
            { label: "Accruewise Assistant", to: "/ai-agents" },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "About us", to: "/about-us" },
            { label: "Blog", to: "/blog" },
            { label: "Join the waitlist", to: "/waitlist" },
            { label: "FAQs", to: "/faqs" },
            { label: "Contact us", to: "/contact-us" },
        ],
    },
    {
        title: "Policies",
        links: [
            { label: "Privacy policy", to: "/privacy-policy" },
            { label: "Terms of service", to: "/terms-and-conditions" },
            { label: "Refund & cancellation", to: "/refund-and-cancellation" },
            { label: "Grievance redressal", to: "/grievance-redressal-policy" },
        ],
    },
];

export default function Footer() {
    return (
        <footer className="relative overflow-hidden border-t border-line bg-canvas pb-24 text-muted lg:pb-0">
            {/* Closing statement on an ink panel: the one dark moment on the page */}
            <div className="section-shell pt-16 md:pt-20">
                <div className="relative mx-auto max-w-7xl overflow-hidden rounded-2xl bg-ink px-6 py-14 sm:px-12 md:py-16">
                    <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-brand/[0.07] blur-3xl" />
                    <div className="relative flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
                        <div className="max-w-xl">
                            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-brand">Free on Android · iPhone soon</p>
                            <h2 className="font-serif text-balance text-3xl font-medium leading-[1.12] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
                                Start with one goal. Watch it add up<span className="text-brand">.</span>
                            </h2>
                        </div>
                        <StoreButtons placement="footer" tone="dark" />
                    </div>
                </div>
            </div>

            <div className="section-shell relative z-10">
                <div className="mx-auto grid max-w-7xl gap-12 py-14 md:grid-cols-[1.3fr_repeat(3,1fr)]">
                    <div>
                        <Link to="/" aria-label="Accruewise home">
                            <Wordmark />
                        </Link>
                        <p className="mt-4 max-w-xs text-sm leading-6 text-muted">
                            Save toward goals, track every spend and pay bills from one app, built in India.
                        </p>
                        <a href="mailto:care@accruewise.in" className="mt-5 inline-flex items-center gap-2 text-sm text-body transition-colors hover:text-ink">
                            <Mail size={16} /> care@accruewise.in
                        </a>
                        <div className="mt-6 flex gap-2">
                            {socials.map(({ name, href, Icon }) => (
                                <a
                                    key={name}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={name}
                                    onClick={() => trackEvent("social_click", { social_network: name })}
                                    className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-card text-ink transition-colors hover:border-line-strong hover:text-brand-pale-ink"
                                >
                                    <Icon size={18} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {columns.map((col) => (
                        <div key={col.title}>
                            <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-ink">{col.title}</h3>
                            <ul className="space-y-3 text-sm">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link to={l.to} className="transition-colors hover:text-ink">
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="footer-watermark" aria-hidden="true">
                    <span className="footer-watermark__text">ACCRUEWISE</span>
                </div>

                <div className="mx-auto max-w-7xl border-t border-line py-8 text-xs leading-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                        <span>© {new Date().getFullYear()} ACCRUEWISE TECHNOLOGIES PRIVATE LIMITED. All rights reserved.</span>
                        <span>Made in India</span>
                    </div>
                    <p className="mt-2 max-w-4xl text-muted/80">
                        Registered office: 24th Floor, Tower B, Alphathum Plot No 1 Sec 90, Nepz Post Office,
                        Gautam Buddha Nagar, Noida, Uttar Pradesh, India, 201305.
                    </p>
                </div>
            </div>
        </footer>
    );
}
